import { OrdersService } from 'wanderu-service-wapi';

// --- Raw types (match API contract — amounts in minor units) ---

interface RawDisplayTotals {
    currency: string;
    discountTotal?: number;
    subtotal: number;
    total: number;
    tripsSubtotal?: number;
}

interface RawAuthorizationTotals {
    currency: string;
    total: number;
}

interface RawLineItemBreakdown {
    displayAmount: number;
    lineItemBreakdownType: LineItemBreakdownType;
    name: string;
}

interface RawLineItem {
    authorizationTotals: RawAuthorizationTotals;
    displayTotals: RawDisplayTotals;
    lineItemBreakdown?: RawLineItemBreakdown[];
    lineItemId: string;
    lineItemType: LineItemType;
    quantity: number;
    sku: string;
}

interface RawDetailedPricing {
    displayTotals: RawDisplayTotals;
    lineItems: RawLineItem[];
    orderId: string;
}

// --- Exported types ---

export interface AuthorizationTotals {
    /** Currency we charge the user in (e.g., "CAD" for Canadian carriers). May differ from displayTotals.currency. */
    currency: string;
    /** Charge amount in major units. */
    total: number;
}

/** The checkout-v1 error envelope — a flat body (no `.error` wrapper), unlike
 *  the legacy orders API's nested response.error shape. Shared across every
 *  public/checkout/v1/* endpoint. */
export interface CheckoutError {
    category: string;
    code: string;
    details?: unknown;
}

/** Request body for POST public/checkout/v1/form — creates the order for the Canopy checkout bridge. */
export interface CreateFormRequest {
    currency: string;
    itineraryId: string;
    locale: string;
    passengerCount: number;
}

/** Normalized pricing data. All monetary amounts are in major units (e.g., dollars for USD). */
export interface DetailedPricing {
    /** Totals in the user's display currency. */
    displayTotals: DisplayTotals;
    lineItems: LineItem[];
    orderId: string;
}

/**
 * Shape of the detailed pricing error nested in the API response at response.error.
 * Type-level only — never instantiated as a runtime object. Describes the
 * shape parsed at runtime in detailedPricingSlice (see parseApiError / KNOWN_PRICING_ERROR_CODES).
 *
 * Note: the service layer (clientApiService) does NOT extract errorCode into
 * the thrown Error's .code property — it only reads codeKey/code. Consumers
 * must read error.responseBody.error.errorCode directly.
 */
export type DetailedPricingError =
    | { errorCategory: 'CLIENT_ERROR'; errorCode: 'SESSION_INVALID' }
    | { errorCategory: 'SYSTEM_ERROR'; errorCode: 'UNKNOWN' };

/**
 * Shape of the discount error nested in the API response at response.error.
 * Type-level only — never instantiated as a runtime object. Describes the
 * shape parsed at runtime in discountSlice (see parseDiscountError / KNOWN_DISCOUNT_ERROR_CODES).
 * Used to derive the DiscountCodeError union (via DiscountCodeError['errorCode']).
 *
 * Note: the service layer (clientApiService) does NOT extract errorCode into
 * the thrown Error's .code property — it only reads codeKey/code. Consumers
 * must read error.responseBody.error.errorCode directly.
 */
export interface DiscountCodeError {
    errorCategory: 'DISCOUNT_CODE_ERROR';
    errorCode: 'EXPIRED_DISCOUNT_CODE' | 'INVALID_CODE';
}

export interface DisplayTotals {
    /** Display currency code (e.g., "USD") — the currency shown to the user. */
    currency: string;
    /** Total discount across all line items, in major units. */
    discountTotal: number;
    /** Pre-discount total, in major units. */
    subtotal: number;
    /** Final total after discounts, in major units. */
    total: number;
    /** Sum of trip line item subtotals (before discounts), in major units. Present when order has >1 line item type. */
    tripsSubtotal?: number;
}

/** A single item in the order. All monetary amounts are in major units (e.g., dollars for USD). */
export interface LineItem {
    /** Totals in the currency we will charge the user. May differ from displayTotals.currency. */
    authorizationTotals: AuthorizationTotals;
    /** Totals in the user's display currency. */
    displayTotals: DisplayTotals;
    lineItemBreakdown: LineItemBreakdown[];
    lineItemId: string;
    /** Type discriminator: "trip" for travel legs, "travel_insurance" for insurance. */
    lineItemType: LineItemType;
    quantity: number;
    sku: string;
}

/** Breakdown of charges within a line item. Amount is in display currency, in major units. */
export interface LineItemBreakdown {
    /** Charge amount in display currency, in major units. */
    displayAmount: number;
    lineItemBreakdownType: LineItemBreakdownType;
    name: string;
}

/** @see wanderu/orders definitions.yaml — DetailedPricingBreakdownItem.lineItemBreakdownType */
export type LineItemBreakdownType =
    | 'carrier_fee'
    | 'carrier_product'
    | 'tax'
    | 'third_party_fee'
    | 'third_party_product'
    | 'wanderu_discount'
    | 'wanderu_fee'
    | 'wanderu_product';

/** @see wanderu/orders definitions.yaml — DetailedPricingLineItem.lineItemType */
export type LineItemType = 'refundable_addon' | 'travel_insurance' | 'trip';

// --- Normalization (minor units -> major units) ---
// Converts minor units to major units by dividing by 100.
// IMPORTANT: This assumes centesimal currencies (2 decimal places).
// Non-centesimal currencies (e.g., JPY with 0 decimals, BHD with 3)
// will produce incorrect values. Replace with currency-aware conversion
// if the orders API begins returning non-centesimal currencies.

// Division by 100 is not exact in IEEE 754 for all integers (e.g., 1/100
// cannot be represented exactly in binary floating point). Precision loss
// from division by 100 is negligible when formatting to 2 decimal places.
// The API is expected to return integer minor units; no rounding is applied.
function fromMinorUnits(amount: number): number {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
        throw new TypeError(
            `[wanderu-orders] Expected numeric minor units, got: ${String(amount)}`,
        );
    }
    return amount / 100;
}

function normalizeDisplayTotals(raw: RawDisplayTotals): DisplayTotals {
    return {
        currency: raw.currency,
        discountTotal: fromMinorUnits(raw.discountTotal ?? 0),
        subtotal: fromMinorUnits(raw.subtotal),
        total: fromMinorUnits(raw.total),
        ...(raw.tripsSubtotal != null && { tripsSubtotal: fromMinorUnits(raw.tripsSubtotal) }),
    };
}

function normalizeAuthorizationTotals(raw: RawAuthorizationTotals): AuthorizationTotals {
    return {
        currency: raw.currency,
        total: fromMinorUnits(raw.total),
    };
}

function normalizeLineItem(raw: RawLineItem): LineItem {
    const displayTotals = normalizeDisplayTotals(raw.displayTotals);
    if (displayTotals.discountTotal < 0) {
        // eslint-disable-next-line no-console -- standalone package; host app handles production logging
        console.warn('[wanderu-orders] Negative discountTotal on lineItem:', {
            discountTotal: displayTotals.discountTotal,
            sku: raw.sku,
        });
    }
    return {
        ...raw,
        authorizationTotals: normalizeAuthorizationTotals(raw.authorizationTotals),
        displayTotals,
        lineItemBreakdown: (raw.lineItemBreakdown ?? []).map((b) => ({
            ...b,
            displayAmount: fromMinorUnits(b.displayAmount),
        })),
    };
}

function normalizeDetailedPricing(raw: RawDetailedPricing): DetailedPricing {
    return {
        ...raw,
        displayTotals: normalizeDisplayTotals(raw.displayTotals),
        lineItems: raw.lineItems.map(normalizeLineItem),
    };
}

// --- API Functions ---

// POST returns no pricing data on success — updated pricing is fetched separately
// via fetchDetailedPricing. On failure, the API error propagates to the caller.
export async function applyDiscountCode(
    orderId: string,
    discountCode: string,
): Promise<void> {
    await OrdersService.post(
        `public/orders/order/${orderId}/discountCode`,
        { body: { discountCode } },
        {},
    );
}

function isCheckoutError(value: unknown): value is CheckoutError {
    return value != null && typeof value === 'object'
        && typeof (value as Record<string, unknown>).code === 'string'
        && typeof (value as Record<string, unknown>).category === 'string';
}

// Creates the order for the Canopy checkout bridge (issue #1077). Only orderId
// is consumed here — the rest of the FormResponse (formConfig, trips, pricing)
// is Canopy's concern via its own GET /form/:orderId page-load fetch.
//
// FormResponse/CheckoutError are both flat bodies (orderId / code+category at
// the top level) per the orders checkout-v1 OpenAPI spec — neither is nested
// under `.result`/`.error` the way the legacy orders endpoints are. The
// shared client's assertResponseHasNoErrors only recognizes the legacy
// nested {error: {...}} shape, so it resolves (does not reject) for a
// CheckoutError response — this function detects that case by body shape
// and re-throws with .responseBody attached so parseCheckoutError
// (canopyCheckoutSlice.ts) can still read code/category.
export async function createCheckoutForm(
    request: CreateFormRequest,
    opts: { signal?: AbortSignal } = {},
): Promise<{ orderId: string }> {
    const response = await OrdersService.post(
        'public/checkout/v1/form',
        { body: request, signal: opts.signal },
        {},
    );
    if (typeof response?.orderId === 'string' && response.orderId.length > 0) {
        return { orderId: response.orderId };
    }
    if (isCheckoutError(response)) {
        const error = new Error(response.code) as Error & { responseBody?: unknown };
        error.responseBody = response;
        throw error;
    }
    throw new Error(
        '[wanderu-orders] Unexpected response shape for createCheckoutForm (missing orderId)',
    );
}

export async function fetchDetailedPricing(
    orderId: string,
    displayCurrency?: string,
): Promise<DetailedPricing> {
    const query = displayCurrency
        ? { displayCurrency }
        : {};
    const response = await OrdersService.get(`public/orders/detailedPricing/${orderId}`, {}, query);
    const raw = response?.result;
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.lineItems)) {
        throw new Error(
            `[wanderu-orders] Unexpected response shape for detailedPricing (orderId: ${orderId})`,
        );
    }
    return normalizeDetailedPricing(raw);
}
